import { Fragment } from 'react'
import { Box, Button, Flex, Text, CSSObject } from '@ritualco/jutsu'
import { useContent } from 'lib/content/react'
import { OrganizationType } from 'lib/generated/api'
import { Organization, Workspace } from 'types/entities'

export type WorkspaceChangeProps = {
  workspaceId: string
  organizationId: string
}

export type WorkspaceSelectorProps = {
  workspaces: Workspace[]
  organizations: Organization[]
  onWorkspaceChange: (ids: WorkspaceChangeProps) => void
}

interface WorkspaceByOrg {
  [index: string]: Array<Workspace>
}

const orgTypeText = {
  [OrganizationType.PropertyManager]: 'pmportal.workspaces.titleText.building',
  [OrganizationType.Company]: 'pmportal.workspaces.titleText.office',
}

export const WorkspaceSelector = ({
  workspaces,
  organizations,
  onWorkspaceChange,
}: WorkspaceSelectorProps) => {
  const { formatMessage } = useContent()

  const workspaceItemBoxStyles: CSSObject = {
    padding: '24px 12px',
    width: '100%',
    border: '1px solid',
    borderColor: 'grayscale.quinary',
    borderRadius: '8px',
    marginBottom: '12px',
  }

  const workspaceImageStyles: CSSObject = {
    width: '36px',
    height: '36px',
    marginRight: '12px',
    borderRadius: '4px',
  }

  // Sort organizations by name
  let sortedOrgs = organizations.sort((a, b) => {
    let textA = a.name ? a?.name.toUpperCase() : ''
    let textB = b.name ? b?.name.toUpperCase() : ''
    return textA < textB ? -1 : textA > textB ? 1 : 0
  })

  // Sort workspaces into organizations

  let workspaceByOrg: WorkspaceByOrg = {}

  sortedOrgs.forEach(({ id }) => {
    if (id) workspaceByOrg[id] = []
  })

  workspaces.forEach(workspace => {
    if (workspace.organizationId) {
      let orgId = workspace.organizationId
      if (orgId != null) {
        if (!(workspace.organizationId in workspaceByOrg)) {
          workspaceByOrg[orgId] = []
        }

        workspaceByOrg[orgId].push(workspace)
      }
    }
  })

  return (
    <Box>
      {sortedOrgs.map(({ id: orgId, name: orgName, type }) => (
        <Fragment key={orgId}>
          {orgId !== undefined && workspaceByOrg[orgId].length > 0 ? (
            workspaceByOrg[orgId].map(
              ({ name, id }: Workspace, index: number) => (
                <Fragment key={`${orgId}-${id}`}>
                  {index === 0 && (
                    <Box marginBottom={3}>
                      <Text
                        color="grayscale.primary"
                        fontWeight="medium"
                        fontSize="small"
                      >
                        {orgName} ({formatMessage({ id: orgTypeText[type] })})
                      </Text>
                    </Box>
                  )}
                  <Flex
                    alignItems="center"
                    sx={workspaceItemBoxStyles}
                    key={id}
                  >
                    <Box>
                      <Text
                        color="grayscale.primary"
                        fontWeight="medium"
                        fontSize="small"
                      >
                        {name}
                      </Text>
                    </Box>
                    <Box ml="auto">
                      <Button
                        ml={2}
                        onClick={() =>
                          onWorkspaceChange({
                            workspaceId: id as string,
                            organizationId: orgId,
                          })
                        }
                      >
                        {formatMessage({ id: 'pmportal.global.open' })}
                      </Button>
                    </Box>
                  </Flex>
                </Fragment>
              )
            )
          ) : (
            <Box marginBottom={3} key={orgId}>
              <Text
                color="grayscale.primary"
                fontWeight="medium"
                fontSize="small"
              >
                {orgName} ({formatMessage({ id: orgTypeText[type] })})
              </Text>
            </Box>
          )}
        </Fragment>
      ))}
    </Box>
  )
}
